import { useAttachmentStore } from 'stores/attachmentStore';
import type { AttachmentFlow } from './types';

export type AttachmentContextHolder = {
  orderUid: string;
  flow: AttachmentFlow;
  readOnly: boolean;
};

export type AttachmentContextHandle = {
  // In-place update preserves stack order, otherwise Details rerendering
  // with a new flow ref would briefly land on top of Edit and turn the next
  // "Add" tap into an unintended Save.
  update: (next: Partial<Omit<AttachmentContextHolder, 'orderUid'>>) => void;
  release: () => void;
};

const stack: AttachmentContextHolder[] = [];

const applyTop = (): void => {
  const { setAttachmentContext } = useAttachmentStore.getState();
  const top = stack.at(-1);
  if (!top) {
    setAttachmentContext();
    return;
  }
  setAttachmentContext(top.orderUid, top.flow, top.readOnly);
};

export const pushAttachmentContext = (
  initial: AttachmentContextHolder
): AttachmentContextHandle => {
  const holder: AttachmentContextHolder = { ...initial };
  stack.push(holder);
  applyTop();

  let released = false;
  return {
    update: (next) => {
      if (released) return;
      let mutated = false;
      if (next.flow !== undefined && next.flow !== holder.flow) {
        holder.flow = next.flow;
        mutated = true;
      }
      if (next.readOnly !== undefined && next.readOnly !== holder.readOnly) {
        holder.readOnly = next.readOnly;
        mutated = true;
      }
      if (mutated && stack.at(-1) === holder) applyTop();
    },
    release: () => {
      if (released) return;
      released = true;
      const idx = stack.lastIndexOf(holder);
      if (idx === -1) return;
      stack.splice(idx, 1);
      applyTop();
    },
  };
};

export const __resetAttachmentContextStackForTests = (): void => {
  stack.length = 0;
  applyTop();
};
